import { useState, useCallback } from 'react';
import { type ScheduleItem } from '@/lib/scheduleUtils';

const LINK_TYPES = ['FS', 'SS'] as const;
type LinkType = (typeof LINK_TYPES)[number];

const LINK_INFO: Record<LinkType, { label: string; desc: string }> = {
  FS: { label: 'Finish to Start', desc: 'The next item starts after this item finishes.' },
  SS: { label: 'Start to Start', desc: 'The next item starts at the same time as this item.' },
};

interface Props {
  show: boolean;
  editItem: ScheduleItem | null;
  includedItems: ScheduleItem[];
  saving: boolean;
  saveError: string;
  onClose: () => void;
  onSave: (predecessorId: string, linkType: LinkType, lagDays: number) => Promise<void>;
}

export default function ScheduleLinkModal({ show, editItem, includedItems, saving, saveError, onClose, onSave }: Props) {
  const [linkForm, setLinkForm] = useState({ predecessor_item_id: '', link_type: 'FS' as LinkType, lag_days: 0 });
  const [justSaved, setJustSaved] = useState(false);
  const [search, setSearch] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);

  const isFinalComplete = (item: ScheduleItem): boolean =>
    item.is_complete === true ||
    item.progress >= 100 ||
    (item.actual_end_date != null && item.actual_end_date !== '');

  const availableItems = includedItems.filter((i) => {
    if (i.id === editItem?.id) return false;
    if (!showCompleted && isFinalComplete(i)) return false;
    const query = search.trim().toLowerCase();
    if (!query) return true;
    return `${i.task_no ?? ''} ${i.title}`.toLowerCase().includes(query);
  });

  const handleClose = useCallback(() => {
    setJustSaved(false);
    setLinkForm({ predecessor_item_id: '', link_type: 'FS', lag_days: 0 });
    setSearch('');
    setShowCompleted(false);
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    if (!linkForm.predecessor_item_id) return;
    setJustSaved(false);
    await onSave(linkForm.predecessor_item_id, linkForm.link_type, linkForm.lag_days);
    setJustSaved(true);
    // Auto-close after showing success
    setTimeout(() => {
      handleClose();
    }, 1500);
  }, [linkForm, onSave, handleClose]);

  if (!show || !editItem) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose}></div>
      <div className="relative bg-white w-full max-w-md rounded-3xl overflow-hidden mx-4">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-[#1A1A1A] font-bold text-base">Add Link to &quot;{editItem.title}&quot;</h3>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-[#374151]"></i>
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Success banner */}
          {justSaved && (
            <div className="flex items-start gap-2 bg-[#F0FDF4] rounded-xl p-3 border border-[#BBF7D0]">
              <i className="ri-check-line text-[#059669] text-base flex-shrink-0 mt-0.5"></i>
              <p className="text-[#059669] text-sm font-medium">Linked schedule item added.</p>
            </div>
          )}

          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1.5">Predecessor Item</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search task name..."
              className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A] mb-2"
              disabled={saving || justSaved}
            />
            <label className="flex items-center gap-2 text-xs text-[#6B7280] mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
                className="w-3.5 h-3.5 accent-[#E8621A]"
                disabled={saving || justSaved}
              />
              Show completed tasks
            </label>
            <select
              value={linkForm.predecessor_item_id}
              onChange={(e) => setLinkForm((p) => ({ ...p, predecessor_item_id: e.target.value }))}
              className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A]"
              disabled={saving || justSaved}
            >
              <option value="">Select a predecessor...</option>
              {availableItems.map((i) => (
                <option key={i.id} value={i.id}>{i.task_no ?? '-'} — {i.title}</option>
              ))}
            </select>
            {availableItems.length === 0 && (
              <p className="text-[11px] text-[#9CA3AF] mt-1.5">No unfinished tasks match your search.</p>
            )}
          </div>
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1.5">Link Type</label>
            <div className="grid grid-cols-2 gap-2">
              {LINK_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => setLinkForm((p) => ({ ...p, link_type: t }))}
                  disabled={saving || justSaved}
                  className={`flex items-start gap-2 px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-colors text-left ${linkForm.link_type === t ? 'border-[#E8621A] bg-[#FEF0E7]' : 'border-gray-100 hover:border-gray-300'} ${saving || justSaved ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div>
                    <span className="text-xs font-bold text-[#1A1A1A]">{t}</span>
                    <span className="text-[9px] text-[#6B7280] block">{LINK_INFO[t].label}</span>
                  </div>
                </button>
              ))}
              <div className="px-1 py-1 col-span-2">
                <p className="text-[9px] text-[#9CA3AF]">{LINK_INFO[linkForm.link_type].desc}</p>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-[#555555] text-xs font-semibold mb-1.5">Lag Days</label>
            <input
              type="number"
              min={-365}
              max={365}
              value={linkForm.lag_days}
              onChange={(e) => setLinkForm((p) => ({ ...p, lag_days: Number(e.target.value) }))}
              className="w-full bg-white border border-[#E0DDD8] rounded-xl px-4 py-3 text-sm text-[#1A1A1A]"
              disabled={saving || justSaved}
            />
            {linkForm.lag_days < 0 && (
              <p className="text-[#F59E0B] text-xs mt-1.5">Negative lag is allowed but working-day negative lag is not fully supported yet.</p>
            )}
          </div>
          {saveError && <p className="text-[#EF4444] text-xs bg-[#FEF2F2] rounded-xl px-3 py-2">{saveError}</p>}
        </div>
        <div className="px-5 py-4 border-t border-gray-100">
          <button
            onClick={handleSave}
            disabled={!linkForm.predecessor_item_id || saving || justSaved}
            className="w-full py-3.5 rounded-xl bg-[#E8621A] text-white text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : null}
            {justSaved ? 'Saved!' : 'Add Link'}
          </button>
        </div>
      </div>
    </div>
  );
}
